import { useEffect, useRef, useState } from "react";
import { t } from "@/i18n-js/instance";
import { BadgeV2 } from "@circle-react-uikit/BadgeV2";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { IconButton } from "@circle-react-uikit/HeaderV3/IconButton";
import { Typography } from "@circle-react-uikit/Typography";
import { i18nRoot, maxChapterTitleLength } from "../constants";
import type { MediaChapterFormData } from "../types";
import { formatTime } from "../utils/timeHelpers";

interface ChapterItemProps {
  chapter: MediaChapterFormData;
  isEditing: boolean;
  isNew: boolean;
  onEditEnd: () => void;
  onEditStart: () => void;
  onJumpToChapter?: (time: number) => void;
  onRemove: () => void;
  onUpdate: (value: MediaChapterFormData) => void;
  thumbnailUrl?: string;
}

export const ChapterItem = ({
  chapter,
  isEditing,
  isNew,
  onEditEnd,
  onEditStart,
  onRemove,
  onUpdate,
  thumbnailUrl,
  onJumpToChapter,
}: ChapterItemProps) => {
  const [localTitle, setLocalTitle] = useState(chapter.title);
  const inputRef = useRef<HTMLInputElement>(null);
  const isClickingButtonRef = useRef(false);

  useEffect(() => {
    if (!inputRef.current || !isEditing) return;

    inputRef.current.focus();
    inputRef.current.select();
  }, [inputRef, isEditing]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const title = localTitle.trim() || chapter.title;

    if (title !== chapter.title) {
      onUpdate({ ...chapter, title });
    }
    setLocalTitle(title);
    onEditEnd();
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setLocalTitle(chapter.title);
    }
    onEditEnd();
  };

  const handleBlur = () => {
    if (isClickingButtonRef.current) {
      isClickingButtonRef.current = false;
      return;
    }
    handleSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="bg-tertiary h-16 w-28 shrink-0">
        {thumbnailUrl ? (
          <button
            className="bg-transparent"
            type="button"
            onClick={() => onJumpToChapter?.(chapter.start_time)}
          >
            <img
              src={thumbnailUrl}
              alt={chapter.title}
              className="size-full object-fill"
              loading="lazy"
            />
          </button>
        ) : (
          <div className="bg-tertiary size-full" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {isEditing ? (
          <div className="flex flex-1 items-center gap-2 border-b border-transparent">
            <input
              ref={inputRef}
              name="title"
              className="bg-primary border-input focus:border-secondary placeholder:text-light -my-1.5 w-full rounded border px-2 py-1 text-sm placeholder:opacity-100 focus:outline-none"
              placeholder={t(`${i18nRoot}.chapters.title_placeholder`)}
              value={localTitle}
              onChange={e => setLocalTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              maxLength={maxChapterTitleLength}
            />
            <IconButton
              type="submit"
              name="16-check"
              className="!border-v2-success border"
              iconClassName="!text-v2-success"
              ariaLabel={t("save_and_close")}
              onClick={handleSubmit}
              onMouseDown={() => (isClickingButtonRef.current = true)}
            />
            <IconButton
              type="button"
              name="16-close"
              className="border-secondary border"
              ariaLabel={t("close")}
              onClick={handleCancel}
              onMouseDown={() => (isClickingButtonRef.current = true)}
            />
          </div>
        ) : (
          <span>
            <button
              type="button"
              className="focus-visible:!outline-secondary hover:border-dark max-w-full cursor-pointer truncate rounded-sm border-b border-dashed border-transparent text-left focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2"
              onClick={onEditStart}
            >
              <Typography.LabelSm weight="semibold">
                {localTitle}
              </Typography.LabelSm>
            </button>
          </span>
        )}
        <div>
          <button
            type="button"
            className="bg-transparent"
            onClick={() => onJumpToChapter?.(chapter.start_time)}
          >
            <BadgeV2
              className="!bg-circle-button !text-circle-button !border-circle-button"
              label={formatTime(chapter.start_time)}
              square
            />
          </button>
        </div>
      </div>
      <Dropdown
        menuButtonEl="div"
        button={
          <IconButton
            name="16-menu-dots-horizontal"
            ariaLabel={t(`${i18nRoot}.chapters.actions.title`)}
          />
        }
        size="small"
      >
        <Dropdown.ItemWithLink onClick={onEditStart} linkType="button">
          {t(`${i18nRoot}.chapters.actions.rename`)}
        </Dropdown.ItemWithLink>
        <Dropdown.ItemWithLink onClick={onRemove} linkType="button" hasDanger>
          {t(`${i18nRoot}.chapters.actions.delete`)}
        </Dropdown.ItemWithLink>
      </Dropdown>
    </div>
  );
};
